import type {DateRangeValue} from '../../types';
import type {Moment} from 'moment-jalaali';

import {cn} from '@ds/helpers';
import moment from 'moment-jalaali';
import {useMemo, useState} from 'react';

import {JALALI_MONTH_NAMES, JALALI_WEEK_DAYS} from '../../utils/constants';
import {convertJalaliToGregorian, getJalaliFirstWeekday} from '../../utils/jalali';

export type RangeCalendarProps = {
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  numberOfMonths?: number;
  showWeekDays?: boolean;
  disableToday?: boolean;
  /** When false, only single day selection is allowed (no range). Default: true */
  allowRange?: boolean;
};

type MonthInfo = {
  year: number;
  month: number;
};

const dayCellBase =
  'relative flex aspect-square appearance-none items-center justify-center border-0 bg-transparent text-base';

function DayCell({
  dayMoment,
  startDate,
  endDate,
  disableToday,
  onDayClick
}: {
  dayMoment: Moment;
  startDate: Moment | null;
  endDate: Moment | null;
  disableToday: boolean;
  onDayClick: (m: Moment) => void;
}) {
  const today = moment();
  const isToday = dayMoment.isSame(today, 'day');
  const isPast = dayMoment.isBefore(today, 'day');
  const isStart = Boolean(startDate && dayMoment.isSame(startDate, 'day'));
  const isEnd = Boolean(endDate && dayMoment.isSame(endDate, 'day'));
  const inRange = Boolean(
    startDate &&
    endDate &&
    dayMoment.isAfter(startDate, 'day') &&
    dayMoment.isBefore(endDate, 'day')
  );
  const isDisabled = isPast || (isToday && disableToday);

  return (
    <button
      type="button"
      disabled={isDisabled}
      data-dp-day=""
      data-selected={isStart || isEnd ? '' : undefined}
      data-in-range={inRange ? '' : undefined}
      onClick={() => onDayClick(dayMoment)}
      className={cn(
        dayCellBase,
        isDisabled ? 'cursor-default text-gray-06' : 'cursor-pointer text-gray-08',
        inRange && 'bg-orange-main/10',
        isStart &&
          'relative z-[1] rounded-[20px] bg-orange-main font-medium text-white after:absolute after:inset-0 after:-z-[1] after:bg-orange-main/10 after:rounded-se-[20px] after:rounded-ee-[20px] after:content-[""]',
        isEnd &&
          'relative z-[1] rounded-[20px] bg-orange-main font-medium text-white after:absolute after:inset-0 after:-z-[1] after:bg-orange-main/10 after:rounded-ss-[20px] after:rounded-es-[20px] after:content-[""]',
        isToday &&
          'before:absolute before:bottom-1 before:size-[5px] before:rounded-full before:bg-orange-main before:content-[""]'
      )}
    >
      {dayMoment.jDate()}
    </button>
  );
}

export function RangeCalendar({
  value,
  onChange,
  numberOfMonths = 6,
  showWeekDays = true,
  disableToday = false,
  allowRange = true
}: RangeCalendarProps) {
  const initialStart = useMemo<Moment | null>(
    () => (value?.start ? moment(value.start, 'YYYY-MM-DD') : null),
    [value?.start]
  );
  const initialEnd = useMemo<Moment | null>(
    () => (value?.end ? moment(value.end, 'YYYY-MM-DD') : null),
    [value?.end]
  );

  const [startDate, setStartDate] = useState<Moment | null>(initialStart);
  const [endDate, setEndDate] = useState<Moment | null>(initialEnd);

  const baseJYear = moment().jYear();
  const baseJMonth = moment().jMonth();

  const months: MonthInfo[] = useMemo(() => {
    const result: MonthInfo[] = [];
    let year = baseJYear;
    let month = baseJMonth;

    for (let i = 0; i < numberOfMonths; i += 1) {
      result.push({year, month});
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }

    return result;
  }, [baseJYear, baseJMonth, numberOfMonths]);

  const emitChange = (start: Moment | null, end: Moment | null) => {
    if (!onChange) return;

    onChange({
      start: start ? convertJalaliToGregorian(start.format('jYYYY-jMM-jDD')) : undefined,
      end: end ? convertJalaliToGregorian(end.format('jYYYY-jMM-jDD')) : undefined
    });
  };

  const handleDayClick = (dayMoment: Moment) => {
    const today = moment();

    if (dayMoment.isSame(today, 'day') && disableToday) return;
    if (dayMoment.isBefore(today, 'day')) return;

    if (!allowRange) {
      setStartDate(dayMoment);
      setEndDate(dayMoment);
      emitChange(dayMoment, dayMoment);
      return;
    }

    if (!startDate || (startDate && endDate)) {
      setStartDate(dayMoment);
      setEndDate(null);
      emitChange(dayMoment, null);
      return;
    }

    if (dayMoment.isBefore(startDate, 'day')) {
      setStartDate(dayMoment);
      setEndDate(startDate);
      emitChange(dayMoment, startDate);
    } else if (dayMoment.isSame(startDate, 'day')) {
      setStartDate(dayMoment);
      setEndDate(null);
      emitChange(dayMoment, null);
    } else {
      setEndDate(dayMoment);
      emitChange(startDate, dayMoment);
    }
  };

  const renderMonth = ({year, month}: MonthInfo) => {
    const daysInMonth = moment.jDaysInMonth(year, month);
    const firstDayOfWeek = getJalaliFirstWeekday(year, month);
    const cells: (Moment | null)[] = [];

    for (let i = 0; i < firstDayOfWeek; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(moment(`${year}-${month + 1}-${day}`, 'jYYYY-jM-jD'));
    }
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div key={`${year}-${month}`} className="mb-12">
        <div className="my-4 mr-[34px] ml-4 text-right text-[1.4em]">
          <span>
            {JALALI_MONTH_NAMES[month]} {year}
          </span>
        </div>
        {showWeekDays && (
          <div className="flex px-4 pb-2">
            {JALALI_WEEK_DAYS.map((w) => (
              <span key={w} className="flex-1 text-center text-gray-06">
                {w}
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-7 px-4 pt-2">
          {cells.map((m, index) =>
            m ? (
              <DayCell
                key={index}
                dayMoment={m}
                startDate={startDate}
                endDate={endDate}
                disableToday={disableToday}
                onDayClick={handleDayClick}
              />
            ) : (
              <div key={index} className={cn(dayCellBase, 'cursor-default')} />
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-4">
      {months.map(renderMonth)}
    </div>
  );
}

export default RangeCalendar;
